/**
 * Users service — core CRUD + listing/search/counts.
 * Ported 1:1 from the legacy flat services/users.js (URL strings preserved
 * byte-for-byte, including param quirks). Response shapes verified against
 * docs/users-migration/Api-response/.
 */
import constants from '../../constants'
import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'
import {
  ChatApp,
  AddUserPayload,
  UpdateUserPayload,
  UsersListFilters,
  UsersListResponse,
  UsersCountResponse,
  UserResponse,
} from './types'

/**
 * LEGACY ERROR CONTRACT (deliberate): resolves with the raw error object on
 * failure instead of rejecting. The frozen apps/* sagas (redux/apps/sagas.js,
 * no try/catch) rely on this to fall through to their empty-list branch on
 * network errors. Normalize only when those consumers retire (Plan Phase 7).
 */
export async function list(
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchText = '',
  onlyDeactivated: 0 | 1 = 0,
  filters: UsersListFilters = {},
): Promise<UsersListResponse> {
  const searchParam = filters.searchKey
    ? encodeURIComponent(filters.searchKey)
    : encodeURIComponent(searchText || '')
  let URL = `${getChatApiBase(app, apiHost)}/users?per_page=${
    constants.perPageInListings
  }&page=${pageNo}&searchKey=${searchParam}&onlyDeactivated=${onlyDeactivated}&useCursoredIndex=1`

  if (filters.uids && filters.uids.length > 0) {
    URL += `&uids=${filters.uids.map(u => encodeURIComponent(u)).join(',')}`
  }
  if (filters.roles && filters.roles.length > 0) {
    URL += `&role=${filters.roles.map(r => encodeURIComponent(r)).join(',')}`
  }
  if (filters.status && filters.status.length > 0) {
    URL += `&status=${encodeURIComponent(filters.status[0])}`
  }
  if (filters.createdAtFrom) {
    URL += `&createdAt[from]=${encodeURIComponent(filters.createdAtFrom)}`
  }
  if (filters.createdAtTo) {
    URL += `&createdAt[to]=${encodeURIComponent(filters.createdAtTo)}`
  }
  if (filters.updatedAtFrom) {
    URL += `&updatedAt[from]=${encodeURIComponent(filters.updatedAtFrom)}`
  }
  if (filters.updatedAtTo) {
    URL += `&updatedAt[to]=${encodeURIComponent(filters.updatedAtTo)}`
  }
  if (filters.includeSystemUsers) {
    URL += `&includeSystemUsers=true`
  }

  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
      params: { _: Date.now() },
    })
    .then(res => res.data)
    .catch(error => error)
}

/**
 * Counts via the list endpoint's `count=1` param. Returns per-presence-status
 * groups; callers sum them (see redux sumStatusCounts). LEGACY ERROR CONTRACT
 * (same as `list` — frozen apps/* sagas depend on it).
 */
export async function count(
  app: ChatApp,
  apiHost: string,
  onlyDeactivated: 0 | 1 = 0,
  filters: UsersListFilters = {},
): Promise<UsersCountResponse> {
  const searchParam = filters.searchKey ? encodeURIComponent(filters.searchKey) : ''
  const URL = `${getChatApiBase(
    app,
    apiHost,
  )}/users?count=1&onlyDeactivated=${onlyDeactivated}&searchKey=${searchParam}`

  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
      params: { _: Date.now() },
    })
    .then(res => res.data)
    .catch(error => error)
}

export async function get(id: string, app: ChatApp, apiHost: string): Promise<UserResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${id}`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
      params: { _: Date.now() },
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function getMultiple(
  ids: string,
  app: ChatApp,
  apiHost: string,
): Promise<UsersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users?uids=${ids}`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function add(
  post: AddUserPayload,
  app: ChatApp,
  apiHost: string,
): Promise<UserResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users`
  return axios
    .post(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function update(
  id: string,
  post: UpdateUserPayload,
  app: ChatApp,
  apiHost: string,
): Promise<UserResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${id}`
  return axios
    .put(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Permanent delete — same URL as deactivate; the `{permanent: true}` body is
 *  the only difference (verified: user-delete-permanent.json). */
export async function del(id: string, app: ChatApp, apiHost: string): Promise<UserResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${id}`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
      data: {
        permanent: true,
      },
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Plain user search. NOTE (legacy-preserved): `searchText` is interpolated
 *  unencoded, exactly like the flat service did. */
export async function search(
  app: ChatApp,
  apiHost: string,
  searchText: string,
  pageNo = 1,
): Promise<UsersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users?searchKey=${searchText}&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
      params: { _: Date.now() },
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** AI-agent users (role=@agentic). LEGACY ERROR CONTRACT (same as `list`). */
export async function aiAgentUserList(
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchText = '',
  onlyDeactivated: 0 | 1 = 0,
  role = '@agentic',
  perPage: string | number = '10',
): Promise<UsersListResponse> {
  const URL = `${getChatApiBase(
    app,
    apiHost,
  )}/users?per_page=${perPage}&page=${pageNo}&searchKey=${searchText}&onlyDeactivated=${onlyDeactivated}&useCursoredIndex=1&role=${role}`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error)
}
